import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setStoredToken } from "@/lib/api";
import { getStoredRef, clearStoredRef } from "@/lib/refTracking";
import { resetPurchases } from "@/lib/iap";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
    // user: null = anonymous, object = authenticated. Use `loading` for the
    // initial "still checking" sentinel — separate from logged-out state to
    // avoid race conditions on logout.
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            console.warn("[auth] checkAuth: not logged in", e?.response?.status);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    // Like checkAuth but doesn't toggle the global `loading` flag — used by
    // post-payment / post-action flows so ProtectedRoute doesn't unmount the
    // current page mid-flow.
    const refreshUser = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            console.warn("[auth] refreshUser failed (keeping current user)", e?.response?.status);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            console.warn("[auth] logout API error (clearing local state anyway)", e?.response?.status);
        }
        // Sign the captain out of RevenueCat too — so if a different captain
        // logs in on this device, their purchases won't be attributed to the
        // previous appUserID. No-op on web.
        try { await resetPurchases(); } catch { /* non-fatal */ }
        setStoredToken(null);
        setUser(null);
    }, []);

    useEffect(() => {
        // If returning from emergent OAuth, AuthCallback handles it — skip /me
        if (
            typeof window !== "undefined" &&
            window.location.hash &&
            window.location.hash.includes("session_id=")
        ) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    // Once a user is authenticated AND we have a stored marketing ref (from a
    // ?ref=marina QR scan etc.), attach it to their account once and clear.
    // No-op if user already has signup_ref or no ref is stored.
    useEffect(() => {
        if (!user) return;
        const ref = getStoredRef();
        if (!ref) return;
        if (user.signup_ref) {
            clearStoredRef();
            return;
        }
        (async () => {
            try {
                await api.post("/auth/attach-ref", { ref });
                clearStoredRef();
            } catch (e) {
                console.warn("[ref] attach failed", e?.response?.status);
            }
        })();
    }, [user]);

    // Memoize context value so consumers don't re-render on every parent render.
    const value = useMemo(
        () => ({ user, setUser, loading, checkAuth, refreshUser, logout }),
        [user, loading, checkAuth, refreshUser, logout],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
